// src/pages/AdminChat.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import FileMessage from '../components/FileMessage';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  FaPaperPlane,
  FaTimes,
  FaComments,
  FaExclamationTriangle,
  FaSync,
  FaSearch,
  FaCalendar,
  FaPaperclip,
  FaRegClock,    
  FaEye,        
  FaRegComment,
  FaSmile,
  FaEllipsisH,
  FaCopy,
  FaEdit,
  FaTrash,
  FaCheck,
  FaCircle
} from 'react-icons/fa';
import { getFirebase, getCurrentUserEnv, getProdDb } from '@services/firebase';
import { onSnapshot, doc } from 'firebase/firestore';
import {
  readAllChats,
  readUserChat,
  addChatMessage,
  subscribeToAllChats,
  initializeUserChat,
  getUserRoleForChat,
  findSuperUserId,
  markChatAsRead,
  writeUserChat,
  deleteMessage,
  updateMessage
} from '@services/firebaseChat';
import { readGlobalProfiles, readUserData } from '@services/firebaseGlobal';
import { getAppTimezone, formatChatTime } from './Settings';
import ChatInsertModal from '../components/modals/ChatInsertModal';
import HomeProfileCardLarge from '../components/HomeProfileCardLarge';
import ProfileRecipientPopup from '../components/ProfileRecipientPopup';
import { useProfileActions } from '../hooks/useProfileActions';

const { auth } = getFirebase();

const AdminChat = ({ userRole: propUserRole }) => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [message, setMessage] = useState('');
  const [userRole, setUserRole] = useState(() => {
    if (propUserRole) return propUserRole;
    try {
      const cachedRole = localStorage.getItem('userRole');
      return cachedRole || '';
    } catch (err) {
      console.error('Error reading userRole from localStorage:', err);
      return '';
    }
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isSendingProfile, setIsSendingProfile] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [searchTerm, setSearchTerm] = useState('');
  const [receiverOnline, setReceiverOnline] = useState(null);
  const [receiverIsActive, setReceiverIsActive] = useState(null);
  const [receiverLastSeenRaw, setReceiverLastSeenRaw] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [showTimeFilters, setShowTimeFilters] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const unsubscribeChatsRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [messageMenuOpen, setMessageMenuOpen] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editMessageText, setEditMessageText] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [copyToastVisible, setCopyToastVisible] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileCard, setShowProfileCard] = useState(false);
  // Pre-read sessionStorage once so both states are initialised correctly
  const _initialShare = (() => {
    try {
      const studioStr = sessionStorage.getItem('chatImageData');
      if (studioStr) {
        const d = JSON.parse(studioStr);
        sessionStorage.removeItem('chatImageData');
        return { image: d.url, profileId: d.fileName, _isStudioImage: true };
      }
      const profileStr = sessionStorage.getItem('chatProfileData');
      if (profileStr) {
        const d = JSON.parse(profileStr);
        sessionStorage.removeItem('chatProfileData');
        return d;
      }
    } catch (_) {}
    return null;
  })();
  const [profileToSend, setProfileToSend] = useState(_initialShare);
  const [showRecipientPopup, setShowRecipientPopup] = useState(!!_initialShare);
  const { favourites, customProfiles, addToFavourites, addToCustom, isInFavourites, isInCustom } = useProfileActions();

  // Check if user is Admin or Super
  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) return;

      try {
        const role = await getUserRoleForChat(user.uid);
        setUserRole(role);
        // Sync to localStorage
        try {
          localStorage.setItem('userRole', role);
        } catch (err) {
          console.error('Error saving userRole to localStorage:', err);
        }

        if (!(role === 'Admin' || role === 'Super')) {
          navigate('/');
        }
      } catch (error) {
        console.error('Error checking user role:', error);
        navigate('/');
      }
    };

    if (!propUserRole) {
      checkUserRole();
    } else if (!(propUserRole === 'Admin' || propUserRole === 'Super')) {
      navigate('/');
    }
  }, [user, navigate, propUserRole]);

  // Load all users for search and chat initialization
  useEffect(() => {
    const loadAllUsers = async () => {
      if (!(userRole === 'Admin' || userRole === 'Super')) return;

      try {
        const userList = await readGlobalProfiles('cUsers');
        const allProfilesRaw = await readUserData('profile');

        const currentEnv = getCurrentUserEnv();
        const usersWithProfiles = userList
          .filter(userData => (userData.env || 'Prod') === currentEnv)
          .map(userData => {
          const profile = allProfilesRaw.find(p => p.id === userData.id);

          let role = 'User';
          if (userData.isadminuser) role = 'Admin';
          else if (userData.issuperuser) role = 'Super';
          else if (userData.isbuyer) role = 'Buyer';
          else if (userData.ismover) role = 'Mover';

          return {
            id: userData.id,
            email: userData.email || profile?.email || 'No email',
            userName: profile?.f_name || 'Unknown',
            userSurname: profile?.l_name || 'User',
            profilePicture: profile?.p_picture || '',
            lastSeen: userData.last_seen ? formatChatTime(userData.last_seen) : 'Never',
            userRole: role
          };
        }).filter(userItem => userItem.id !== user?.uid);

        setAllUsers(usersWithProfiles);
      } catch (error) {
        console.error('Error loading all users:', error);
      }
    };

    if (userRole === 'Admin' || userRole === 'Super') {
      loadAllUsers();
    }
  }, [userRole, user?.uid]);

  // Add this useEffect to close message menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (messageMenuOpen && !event.target.closest('.message-options-menu') && !event.target.closest('.message-options-btn')) {
        setMessageMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [messageMenuOpen]);


  // Function to send profile image message
  const sendProfileImageMessage = async (profileData, recipientUser) => {    
    if (!user || !profileData || !profileData.image || !recipientUser) {
      return;
    }

    try {
      setSending(true);
      setIsSendingProfile(true);
      setError(null);

      let messageText;
      let fileData;

      if (profileData._isStudioImage) {
        // Studio image � plain file message, no profileData template
        messageText = 'Image Studio';
        fileData = {
          downloadUrl: profileData.image,
          name: profileData.profileId || 'studio-image.jpg',
          size: 0,
          mimeType: (profileData.profileId || '').endsWith('.png') ? 'image/png' : 'image/jpeg',
        };
      } else {
        // Home profile card � include profileData so recipient can open the card
        const description = [];
        if (profileData.theme) description.push(`Theme: ${profileData.theme}`);
        if (profileData.flavour) description.push(`Flavour: ${profileData.flavour}`);
        if (profileData.filling) description.push(`Filling: ${profileData.filling}`);
        if (profileData.cover) description.push(`Cover: ${profileData.cover}`);
        messageText = `Profile Reference\n${description.join('\n')}`;
        fileData = {
          downloadUrl: profileData.image,
          name: `profile_${profileData.profileId || 'image'}.jpg`,
          size: 0,
          type: 'image/jpeg',
          profileData: profileData,
        };
      }

      const messageData = {
        text: messageText,
        senderId: user.uid,
        senderRole: userRole,
        receiverId: recipientUser.id,
        type: 'file',
        timestamp: new Date().toISOString(),
        fileData,
      };
      await addChatMessage(user.uid, messageData);
      
      // If we're currently in a chat with this recipient, switch to it
      if (selectedChat?.id === recipientUser.id) {
        // We're already in the chat, no need to switch
      } else {
        // Find if this recipient already exists in chats
        const existingChat = chats.find(chat => chat.id === recipientUser.id);
        if (existingChat) {
          setSelectedChat(existingChat);
          setSelectedChatId(recipientUser.id);
        } else {
          // Start a new chat with this recipient
          await startChat(recipientUser);
        }
      }

    } catch (error) {
      console.error('? Error sending profile image to chat:', error);
      setError('Failed to send profile image: ' + error.message);
    } finally {
      setSending(false);
      setIsSendingProfile(false);
    }
  };

  // Handle profile image click to open large card
  const handleProfileImageClick = useCallback((message) => {
    if (message.type === 'file' && message.fileData?.profileData) {
      setSelectedProfile(message.fileData.profileData);
      setShowProfileCard(true);
    }
  }, []);

  // Handle closing profile card
  const handleCloseProfileCard = useCallback(() => {
    setShowProfileCard(false);
    setSelectedProfile(null);
  }, []);

  // Handle sending profile from external source (like home profile card)
  const handleSendProfile = useCallback((profileData) => {
    setProfileToSend(profileData);
    setShowRecipientPopup(true);
  }, []);

  // Handle recipient selection for profile sending
  const handleRecipientSelect = useCallback((recipientUser) => {
    if (profileToSend) {
      sendProfileImageMessage(profileToSend, recipientUser);
      setProfileToSend(null);
    }
  }, [profileToSend]);

  // Process Super chat to extract conversations
  const processSuperChat = useCallback(async (allChats, currentUserId) => {
    if (!allChats || allChats.length === 0) return [];

    const participantMap = new Map();

    allChats.forEach(chat => {
      if (!chat?.messages) return;

      const isSupportChat = chat.chatWith === 'support';

      chat.messages.forEach(message => {
        let otherUserId = null;

        if (message.senderId === currentUserId) {
          otherUserId = message.receiverId;
        }
        else if (message.receiverId === currentUserId) {
          otherUserId = message.senderId;
        }
        else if (isSupportChat && (message.senderRole === 'Admin' || message.senderRole === 'Super')) {
          otherUserId = message.receiverId;
        }
        else if (isSupportChat && (message.senderRole !== 'Admin' && message.senderRole !== 'Super')) {
          otherUserId = message.senderId;
        }
        else if (message.senderRole === 'Admin' && message.receiverRole === 'Super') {
          otherUserId = message.senderId;
        }

        if (otherUserId && otherUserId !== currentUserId) {
          if (!participantMap.has(otherUserId)) {
            participantMap.set(otherUserId, {
              messages: [],
              sourceChats: new Set(),
              isSupportChat: isSupportChat,
              bookmarked: chat.bookmarked || false
            });
          }
          const participantData = participantMap.get(otherUserId);
          participantData.messages.push({
            ...message,
            sourceChatId: chat.id
          });
          participantData.sourceChats.add(chat.id);
          participantData.isSupportChat = participantData.isSupportChat || isSupportChat;
          if (chat.bookmarked) {
            participantData.bookmarked = true;
          }
        }
      });
    });

    const participantChats = await Promise.all(
      Array.from(participantMap.entries()).map(async ([participantId, chatData]) => {
        try {
          const userList = await readGlobalProfiles('cUsers');
          const allProfilesRaw = await readUserData('profile');

          const userData = userList?.find(u => u && u.id === participantId);
          const profile = allProfilesRaw?.find(p => p && p.id === participantId);

          let participantRole = 'User';
          if (userData) {
            if (userData.isadminuser) participantRole = 'Admin';
            else if (userData.issuperuser) participantRole = 'Super';
            else if (userData.isbuyer) participantRole = 'Buyer';
            else if (userData.ismover) participantRole = 'Mover';
          }

          const sortedMessages = chatData.messages.sort((a, b) =>
            new Date(a.timestamp) - new Date(b.timestamp)
          );

          const lastMessage = sortedMessages.length > 0 ?
            sortedMessages.find(msg => msg.lastMessage) || sortedMessages[sortedMessages.length - 1] :
            null;

          return {
            id: participantId,
            userName: profile?.f_name || 'Unknown User',
            userSurname: profile?.l_name || '',
            profilePicture: profile?.p_picture || '',
            lastSeen: userData?.last_seen ? formatChatTime(userData.last_seen) : 'Never',
            lastSeenRaw: userData?.last_seen || null,
            isActiveRaw: userData?.isactive ?? null,
            userRole: participantRole,
            email: profile?.email || 'No email',
            messages: sortedMessages,
            lastMessage: lastMessage,
            isVirtual: true,
            bookmarked: chatData.bookmarked || false,
            sourceChats: Array.from(chatData.sourceChats),
            updated: lastMessage?.timestamp || new Date().toISOString()
          };
        } catch (error) {
          console.error('Error processing participant:', participantId, error);
          return null;
        }
      })
    );

    return participantChats.filter(chat => chat !== null);
  }, []);

  // Process Admin chats from multiple sources
  const processAdminChats = useCallback(async (allChats, currentUserId) => {
    const superUserId = await findSuperUserId();
    const superChat = allChats.find(chat => chat.id === superUserId);
    const regularChats = allChats.filter(chat =>
      chat.userRole === 'Mover' || chat.userRole === 'Buyer' || chat.userRole === 'User'
    );

    const enhancedRegularChats = await Promise.all(
      regularChats.map(async (chat) => {
        try {
          if (chat.userName === 'Unknown User' || !chat.userName) {
            const userList = await readGlobalProfiles('cUsers');
            const allProfilesRaw = await readUserData('profile');

            const userData = userList?.find(u => u && u.id === chat.id);
            const profile = allProfilesRaw?.find(p => p && p.id === chat.id);

            if (profile || userData) {
              return {
                ...chat,
                userName: profile?.f_name || 'Unknown User',
                userSurname: profile?.l_name || '',
                profilePicture: profile?.p_picture || '',
                email: profile?.email || chat.email || 'No email',
                bookmarked: chat.bookmarked || false,
                lastSeen: userData?.last_seen ? formatChatTime(userData.last_seen) : 'Never',
                lastSeenRaw: userData?.last_seen || null,
                isActiveRaw: userData?.isactive ?? null
              };
            }
          }
          return chat;
        } catch (error) {
          console.error('Error enhancing chat data:', chat.id, error);
          return chat;
        }
      })
    );

    let processedChats = [...enhancedRegularChats];

    if (superChat?.messages) {
      const adminParticipants = new Set();

      superChat.messages.forEach(message => {
        if (message.senderId === currentUserId || message.receiverId === currentUserId) {
          const otherUserId = message.senderId === currentUserId ? message.receiverId : message.senderId;
          if (otherUserId && otherUserId !== currentUserId) {
            adminParticipants.add(otherUserId);
          }
        }
      });

      for (const participantId of adminParticipants) {
        try {
          const userList = await readGlobalProfiles('cUsers');
          const allProfilesRaw = await readUserData('profile');

          const userData = userList?.find(u => u && u.id === participantId);
          const profile = allProfilesRaw?.find(p => p && p.id === participantId);

          let participantRole = 'User';
          if (userData) {
            if (userData.isadminuser) participantRole = 'Admin';
            else if (userData.issuperuser) participantRole = 'Super';
            else if (userData.isbuyer) participantRole = 'Buyer';
            else if (userData.ismover) participantRole = 'Mover';
          }

          const participantMessages = superChat.messages.filter(msg =>
            (msg.senderId === currentUserId && msg.receiverId === participantId) ||
            (msg.senderId === participantId && msg.receiverId === currentUserId)
          );

          const lastMessage = participantMessages.length > 0 ?
            participantMessages.find(msg => msg.lastMessage) || participantMessages[participantMessages.length - 1] :
            null;

          const virtualChat = {
            id: participantId,
            userName: profile?.f_name || 'Unknown User',
            userSurname: profile?.l_name || '',
            profilePicture: profile?.p_picture || '',
            lastSeen: userData?.last_seen ? formatChatTime(userData.last_seen) : 'Never',
            lastSeenRaw: userData?.last_seen || null,
            isActiveRaw: userData?.isactive ?? null,
            userRole: participantRole,
            email: profile?.email || 'No email',
            messages: participantMessages,
            lastMessage: lastMessage,
            isVirtual: true,
            sourceSuperChat: superChat.id,
            bookmarked: superChat.bookmarked || false,
            updated: superChat.updated
          };

          const existingIndex = processedChats.findIndex(chat => chat.id === participantId);
          if (existingIndex !== -1) {
            processedChats[existingIndex] = virtualChat;
          } else {
            processedChats.push(virtualChat);
          }
        } catch (error) {
          console.error('Error processing Admin participant:', participantId, error);
        }
      }
    }

    return processedChats;
  }, []);

  const toggleBookmark = async (chatId, e) => {
  e.stopPropagation();
  
    try {
      const chatUserRole = await getUserRoleForChat(chatId);
      let targetChatId = chatId;

      // If this is an Admin chat, find the Super chat document
      if (chatUserRole === 'Admin') {        
        const allChats = await readAllChats(user.uid);
        const superChat = allChats.find(chat => 
          chat.messages?.some(msg => 
            // Messages between current Super user and this Admin user
            ((msg.senderId === chatId && msg.receiverId === user.uid) ||
            (msg.senderId === user.uid && msg.receiverId === chatId))
          )
        );

        if (superChat) {
          targetChatId = superChat.id;
        } else {
          return;
        }
      }
      const chatDoc = await readUserChat(targetChatId);
      if (!chatDoc) return;

      // Toggle the bookmark flag in the chat document
      const updatedChat = {
        ...chatDoc,
        bookmarked: !chatDoc.bookmarked,
        updated: new Date().toISOString()
      };

      await writeUserChat(targetChatId, updatedChat);

      if (chatDoc.bookmarked) {
      // If we're unbookmarking (was true, now false), remove from active chats
        setChats(prev => prev.filter(chat => chat.id !== chatId));
      } else {
        // If we're bookmarking (was false, now true), update the flag
        setChats(prev => prev.map(chat => 
          chat.id === chatId ? { ...chat, bookmarked: true } : chat
        ));
      }

    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  // Load chats and setup real-time listener
  const loadChats = useCallback(async () => {
    if (!(userRole === 'Admin' || userRole === 'Super') || !user) return;

    try {
      setLoading(true);
      setError(null);

      const allChats = await readAllChats(user.uid);

      let processedChats = [];

      if (userRole === 'Admin') {
        processedChats = await processAdminChats(allChats, user.uid);
      } else {
        processedChats = await processSuperChat(allChats, user.uid);
      }
      const bookmarkedChats = processedChats.filter(chat => chat.bookmarked === true);
      setChats(bookmarkedChats);

    } catch (error) {
      console.error('Error loading chats:', error);
      setError('Failed to load chats. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userRole, user?.uid, processAdminChats, processSuperChat]);

  // Setup real-time listener
  useEffect(() => {
    if (!(userRole === 'Admin' || userRole === 'Super') || !user) return;

    unsubscribeChatsRef.current?.();

    unsubscribeChatsRef.current = subscribeToAllChats(user.uid, async (updatedChats) => {
      setConnectionStatus('online');

      let realTimeProcessedChats = [];

      if (userRole === 'Admin') {
        realTimeProcessedChats = await processAdminChats(updatedChats, user.uid);
      } else {
        realTimeProcessedChats = await processSuperChat(updatedChats, user.uid);
      }

      // Only show chats that are bookmarked (bookmarked: true)
      const bookmarkedChats = realTimeProcessedChats.filter(chat => chat.bookmarked === true);
      setChats(bookmarkedChats);
    });

    return () => {
      unsubscribeChatsRef.current?.();
    };
  }, [userRole, user?.uid, processAdminChats, processSuperChat]);

  // Sync selectedChat from real-time chats updates
  useEffect(() => {
    if (!selectedChat?.id) { setReceiverOnline(null); setReceiverIsActive(null); setReceiverLastSeenRaw(null); return; }
    const unsub = onSnapshot(doc(getProdDb(), 'cUsers', selectedChat.id), (snap) => {
      if (!snap.exists()) { setReceiverOnline(false); setReceiverIsActive(false); setReceiverLastSeenRaw(null); return; }
      const data = snap.data();
      const lastSeen = data?.last_seen || null;
      const isActive = data?.isactive ?? false;
      setReceiverIsActive(isActive);
      setReceiverLastSeenRaw(lastSeen);
      if (!isActive) { setReceiverOnline(false); return; }
      if (!lastSeen) { setReceiverOnline(false); return; }
      setReceiverOnline(Date.now() - new Date(lastSeen).getTime() < 5 * 60 * 1000);
    });
    return () => unsub();
  }, [selectedChat?.id]);

  // Sync selectedChat from real-time chats updates (messages)
  useEffect(() => {
    if (selectedChatId && chats.length > 0) {
      const updatedSelected = chats.find(c => c.id === selectedChatId);
      if (updatedSelected) {
        if (!selectedChat || selectedChat.messages.length !== updatedSelected.messages.length || selectedChat.updated !== updatedSelected.updated) {
          setSelectedChat(updatedSelected);
        }
      }
    }
  }, [chats, selectedChatId, selectedChat?.messages.length, selectedChat?.updated]);

  useEffect(() => {
    if (!selectedChatId) return;

    // Find the freshest version of the chat that lives in the `chats` array.
    const freshChat = chats.find((c) => c.id === selectedChatId);

    if (freshChat) {
      // Always replace � we don�t care whether only the text changed.
      setSelectedChat(freshChat);
    }
  }, [chats, selectedChatId]); 

  // Initial load
  useEffect(() => {
    if (userRole === 'Admin' || userRole === 'Super') {
      loadChats();
    }
  }, [userRole, loadChats]);

  // FIXED: Continuous mark as read while chat is open
  useEffect(() => {
    const markAsRead = async () => {
      if (selectedChat && user) {
        try {
          await markChatAsRead(selectedChat.id, user.uid);

          // Force notification refresh
          if (window.chatNotificationService) {
            setTimeout(() => {
              window.chatNotificationService.forceRefresh(user.uid);
            }, 300);
          }
        } catch (error) {
          console.error('Failed to mark messages as read:', error);
        }
      }
    };

    markAsRead();
  }, [selectedChat, user]);

  // FIXED: Real-time check for Super reading Admin messages
useEffect(() => {
  if (selectedChat && user) {
    const checkForNewMessages = () => {
      if (selectedChat.messages) {
        let unreadMessages = [];
        
        // Super reading Admin messages
        if (userRole === 'Super' && selectedChat.userRole === 'Admin') {
          unreadMessages = selectedChat.messages.filter(msg => 
            !msg.read && msg.senderRole === 'Admin' && msg.receiverId === user.uid
          );
        }
        // Admin reading Super messages  
        else if (userRole === 'Admin' && selectedChat.userRole === 'Super') {
          unreadMessages = selectedChat.messages.filter(msg => 
            !msg.read && msg.senderId === selectedChat.id && msg.receiverId === user.uid
          );
        }
        // Other cases
        else {
          unreadMessages = selectedChat.messages.filter(msg => 
            !msg.read && msg.receiverId === user.uid
          );
        }

        if (unreadMessages.length > 0) {
          markChatAsRead(selectedChat.id, user.uid);
        }
      }
    };

    checkForNewMessages();
    const interval = setInterval(checkForNewMessages, 2000);
    return () => clearInterval(interval);
  }
}, [selectedChat, user, userRole]);

 const startChat = async (chatUser) => {
  try {
    setError(null);
    setSearchTerm('');
    const searchResult = searchResults.find(result => result.id === chatUser.id);
    let existingFirebaseChat;
    
    // If we found existing chat data from search, use it
    if (searchResult?.hasExistingChat && searchResult.existingChat) {
      existingFirebaseChat = searchResult.existingChat;
    } 
    // Otherwise, check Firebase directly (for Admin users or if search didn't find it)
    else if (chatUser.userRole === 'Admin') {
      
      // For Admin users, check the Super user's chat document
      const superUserId = await findSuperUserId();
      if (superUserId) {
        const superChat = await readUserChat(superUserId);
        if (superChat?.messages) {
          // Look for existing messages with this Admin user
          const adminMessages = superChat.messages.filter(msg => 
            (msg.senderId === chatUser.id && msg.receiverId === user?.uid) ||
            (msg.senderId === user?.uid && msg.receiverId === chatUser.id)
          );
          
          if (adminMessages.length > 0) {
            existingFirebaseChat = {
              ...superChat,
              id: chatUser.id, // Use Admin user's ID for the virtual chat
              messages: adminMessages,
              isFromSuperProfile: true,
              sourceSuperChatId: superUserId
            };
          }
        }
      }
    } else {
      // For regular users, check their own chat document
      existingFirebaseChat = await readUserChat(chatUser.id);
    }
    
    let chatToSelect;
    
    if (existingFirebaseChat) {
      // Chat exists - use the real data
      
      // Enhance with user profile data
      chatToSelect = {
        ...existingFirebaseChat,
        userName: chatUser.userName || existingFirebaseChat.userName,
        userSurname: chatUser.userSurname || existingFirebaseChat.userSurname,
        profilePicture: chatUser.profilePicture || existingFirebaseChat.profilePicture,
        lastSeen: chatUser.lastSeen,
        lastSeenRaw: chatUser.lastSeenRaw ?? existingFirebaseChat.lastSeenRaw ?? null,
        isActiveRaw: chatUser.isActiveRaw ?? existingFirebaseChat.isActiveRaw ?? null,
        userRole: chatUser.userRole || existingFirebaseChat.userRole,
        email: chatUser.email || existingFirebaseChat.email,
        isVirtual: chatUser.userRole === 'Admin' // Virtual for Admin users (from Super profile)
      };
      
      // Update the chat in our local state if it's not already there
      setChats(prev => {
        const existingInState = prev.find(chat => chat.id === chatUser.id);
        if (existingInState) {
          return prev.map(chat => chat.id === chatUser.id ? chatToSelect : chat);
        } else {
          return [chatToSelect, ...prev];
        }
      });
      
    } else {
      // No existing chat found - create a new virtual chat
            
      chatToSelect = {
        id: chatUser.id,
        userName: chatUser.userName,
        userSurname: chatUser.userSurname,
        profilePicture: chatUser.profilePicture,
        lastSeen: chatUser.lastSeen,
        lastSeenRaw: chatUser.lastSeenRaw ?? null,
        isActiveRaw: chatUser.isActiveRaw ?? null,
        userRole: chatUser.userRole,
        email: chatUser.email,
        messages: [], // Empty for new chat
        isVirtual: true,
        bookmarked: true,
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        chatWith: (chatUser.userRole !== 'Admin' && chatUser.userRole !== 'Super') ? 'support' : null
      };
      
      // Only save to Firebase for non-Admin users
      if (chatUser.userRole !== 'Admin') {
        await writeUserChat(chatUser.id, chatToSelect);
      }       
      setChats(prev => [chatToSelect, ...prev]);
    }

    setSelectedChat(chatToSelect);
    setSelectedChatId(chatToSelect.id);

    // Mark as read when starting chat
    if (user) {
      await markChatAsRead(chatToSelect.id, user.uid);
    }

    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);

  } catch (error) {
    console.error('Error starting chat:', error);
    setError('Failed to start chat: ' + error.message);
  }
};

  // Send message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !user || !selectedChat) return;

    try {
      setSending(true);
      setError(null);
      setShowEmojiPicker(false);

      const messageData = {
        text: message.trim(),
        senderId: user.uid,
        senderRole: userRole,
        receiverId: selectedChat.id,
        type: 'text'
      };

      await addChatMessage(user.uid, messageData);
      setMessage('');

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);

    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  // Handle file attachment
  const handleAttachmentClick = () => {
    setIsInsertModalOpen(true);
    setShowEmojiPicker(false);
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Handle file insertion
  const handleFileInsert = async (fileData) => {
    if (!user || !selectedChat) return;

    try {
      setSending(true);
      setError(null);

      const messageData = {
        text: `${fileData.name}`,
        senderId: user.uid,
        senderRole: userRole,
        receiverId: selectedChat.id,
        type: 'file',
        timestamp: new Date().toISOString(),
        fileData: {
          downloadUrl: fileData.downloadUrl,
          name: fileData.name,
          size: fileData.size,
          type: fileData.type,
          mimeType: fileData.mimeType,
          bucketPath: fileData.bucketPath
        }
      };

      await addChatMessage(user.uid, messageData);

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);

    } catch (error) {
      console.error('Error sending file message:', error);
      setError('Failed to send file: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  // Format file size helper function
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && 
          !event.target.closest('.emoji-picker-container') && 
          !event.target.closest('.emoji-btn-inside')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Get display name with role for Admin/Super only
  const getDisplayName = (message, isOwnMessage) => {
    if (isOwnMessage) return 'You';

    if (message.senderRole === 'Admin' || message.senderRole === 'Super') {
      return message.senderRole;
    }

    return 'User';
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    if (!messages || messages.length === 0) return [];

    const grouped = [];
    let currentGroup = null;

    messages.forEach((message, index) => {
      const messageDate = new Date(message.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let dateLabel = '';

      if (!currentGroup ||
        currentGroup.date.getDate() !== messageDate.getDate() ||
        currentGroup.date.getMonth() !== messageDate.getMonth() ||
        currentGroup.date.getFullYear() !== messageDate.getFullYear()) {

        if (timeFilter === 'lastWeek' || timeFilter === 'lastMonth' || timeFilter === 'lastYear') {
          dateLabel = messageDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        } else {
          if (messageDate.toDateString() === today.toDateString()) {
            dateLabel = 'Today';
          } else if (messageDate.toDateString() === yesterday.toDateString()) {
            dateLabel = 'Yesterday';
          } else {
            dateLabel = messageDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            });
          }
        }

        currentGroup = {
          date: messageDate,
          label: dateLabel,
          messages: []
        };
        grouped.push(currentGroup);
      }

      currentGroup.messages.push(message);
    });

    return grouped;
  };

  // Filter messages based on time filter and search
  const getFilteredMessages = () => {
    if (!selectedChat?.messages) return [];

    let filtered = [...selectedChat.messages];

    if (timeFilter !== 'all') {
      const now = new Date();
      const filterStartDate = new Date();
      const filterEndDate = new Date();

      switch (timeFilter) {
        case 'lastWeek':
          filterStartDate.setDate(now.getDate() - 14);
          filterEndDate.setDate(now.getDate() - 7);
          break;
        case 'lastMonth':
          filterStartDate.setMonth(now.getMonth() - 2);
          filterEndDate.setMonth(now.getMonth() - 1);
          break;
        case 'lastYear':
          filterStartDate.setFullYear(now.getFullYear() - 2);
          filterEndDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          break;
      }

      filtered = filtered.filter(msg => {
        const msgDate = new Date(msg.timestamp);
        return msgDate >= filterStartDate && msgDate <= filterEndDate;
      });
    }

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter(msg =>
        msg.text.toLowerCase().includes(query) ||
        new Date(msg.timestamp).toLocaleDateString().includes(query)
      );
    }

    return filtered;
  };

  // Get last message for display
  const getLastMessage = (chat) => {
    if (!chat.messages || chat.messages.length === 0) {
      return { text: 'No messages yet', timestamp: null };
    }

    const lastMsg = chat.messages.find(msg => msg.lastMessage === true) ||
      chat.messages[chat.messages.length - 1];
    return lastMsg;
  };

  

  // Filter chats based on search
  const filteredChats = chats.filter(chat => {
    if (!selectedChat && timeFilter !== 'all') {
      if (!chat.messages || chat.messages.length === 0) return false;
      
      const now = new Date();
      const filterStartDate = new Date();
      const filterEndDate = new Date();

      switch (timeFilter) {
        case 'lastWeek':
          filterStartDate.setDate(now.getDate() - 14);
          filterEndDate.setDate(now.getDate() - 7);
          break;
        case 'lastMonth':
          filterStartDate.setMonth(now.getMonth() - 2);
          filterEndDate.setMonth(now.getMonth() - 1);
          break;
        case 'lastYear':
          filterStartDate.setFullYear(now.getFullYear() - 2);
          filterEndDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          return true;
      }

      const hasMessagesInPeriod = chat.messages.some(msg => {
        const msgDate = new Date(msg.timestamp);
        return msgDate >= filterStartDate && msgDate <= filterEndDate;
      });
      
      return hasMessagesInPeriod;
    }

    // If in individual chat view or timeFilter is 'all', don't filter by time here
    return true;
  })
  .filter(chat => {
    if (!searchTerm.trim()) return true;

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      const displayName = `${chat.userName} ${chat.userSurname}`.toLowerCase();
      const email = (chat.email || '').toLowerCase();
      const userRole = (chat.userRole || '').toLowerCase();

      return displayName.includes(query) || email.includes(query) || userRole.includes(query);
    }
  
    // If not searching, only show bookmarked chats
    return true;
  })
  .sort((a, b) => new Date(b.updated) - new Date(a.updated));

// Separate useEffect to handle search with Firebase checks
useEffect(() => {
  const performSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const query = searchTerm.toLowerCase().trim();
      
      // Filter users by search term
      const filteredUsers = allUsers.filter(userItem => {
        const displayName = `${userItem.userName || ''} ${userItem.userSurname || ''}`.toLowerCase().trim();
        const email = String(userItem.email || '').toLowerCase().trim();
        const userRole = String(userItem.userRole || '').toLowerCase().trim();

        return displayName.includes(query) || email.includes(query) || userRole.includes(query);
      });

      // Check Firebase for EACH user to see if they have existing chats
      const usersWithChatStatus = await Promise.all(
        filteredUsers.map(async (userItem) => {
          try {
            // Check Firebase directly for existing chat
            const existingFirebaseChat = await readUserChat(userItem.id);

            return {
              ...userItem,
              hasExistingChat: !!existingFirebaseChat,
              existingChat: existingFirebaseChat,
              firebaseMessages: existingFirebaseChat?.messages?.length || 0
            };
          } catch (error) {
            console.error('Error checking Firebase for user:', userItem.id, error);
            return {
              ...userItem,
              hasExistingChat: false,
              existingChat: null,
              firebaseMessages: 0
            };
          }
        })
      );

      setSearchResults(usersWithChatStatus);
      
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    }
  };

  performSearch();
}, [searchTerm, allUsers]);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [selectedChat?.messages]);

  const handleClose = () => {
    navigate(-1);
  };

  const handleCloseChat = () => {
    setSelectedChat(null);
    setSelectedChatId(null);
    setSearchTerm('');
    // Remove focus from the close button so it doesn't appear "selected" on the landing page
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const retryConnection = () => {
    window.location.reload();
  };

  // Helper functions
  const getUserDisplayName = (user) => {
    return `${user.userName || 'Unknown'} ${user.userSurname || 'User'}`;
  };

  const getProfileImage = (profilePicture) => {
    return profilePicture && profilePicture.trim() !== '' ? profilePicture : null;
  };

  if (!user || !(userRole === 'Admin' || userRole === 'Super')) {
    return null;
  }

  // Handle message options click
  const handleMessageOptionsClick = (messageId, event) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMessageMenuOpen({
      messageId
    });
  };

  // Copy message to clipboard
  const handleCopyMessage = async (message) => {
    try {
      const textToCopy = message.type === 'file' ? message.fileData?.name || message.text : message.text;
      await navigator.clipboard.writeText(textToCopy);
      setMessageMenuOpen(null);
      setCopyToastVisible(true);
      setTimeout(() => setCopyToastVisible(false), 2000); // Hide after 2 seconds
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  // Start editing a message
  const handleEditMessage = (message) => {
    setEditMessageText(message.text);
    setEditingMessageId(message.id);
    setMessageMenuOpen(null);
  };

  // Start editing a message
  const handleDeleteMessage = async (message) => {
    try {
      // Get the correct target chat ID based on the message structure
      let targetChatId = selectedChat.id;
      
      // For messages between Admin and Super, we need to determine the correct storage location
      if ((userRole === 'Admin' && selectedChat.userRole === 'Super') || 
          (userRole === 'Super' && selectedChat.userRole === 'Admin')) {
        const superUserId = await findSuperUserId();
        targetChatId = superUserId || selectedChat.id;
      }

      await deleteMessage(targetChatId, message.id);
      setDeleteConfirmId(null);
      setMessageMenuOpen(false);

      const refreshed = await readUserChat(targetChatId);

      const updatedChat = { ...refreshed, isVirtual: selectedChat.isVirtual };
      setSelectedChat(updatedChat);

      // also update the chats list so the preview stays in sync
      setChats((prev) =>
        prev.map((c) => (c.id === updatedChat.id ? updatedChat : c))
      );

    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message');
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditMessageText('');
  };

  // Save edited message
  const handleSaveEdit = async (message) => {
    if (!editMessageText.trim()) {
      handleCancelEdit();
      return;
    }
    try {
      // Get the correct target chat ID based on the message structure
      let targetChatId = selectedChat.id;
      // For messages between Admin and Super, we need to determine the correct storage location
      if ((userRole === 'Admin' && selectedChat.userRole === 'Super') || 
          (userRole === 'Super' && selectedChat.userRole === 'Admin')) {
        const superUserId = await findSuperUserId();
        targetChatId = superUserId || selectedChat.id;
      }

      await updateMessage(targetChatId, message.id, { text: editMessageText.trim() });

      // ----- REFRESH THE CURRENT CHAT -----

      const newMessages = selectedChat.messages.map((msg) =>
        msg.id === message.id
          ? { ...msg, text: editMessageText.trim(), isEdited: true }
          : msg
      );

      const updatedChat = {...selectedChat, messages: newMessages,};

      setSelectedChat(updatedChat);

      setChats((prev) =>
        prev.map((c) => (c.id === updatedChat.id ? updatedChat : c))
      );

      handleCancelEdit();

    } catch (error) {
      console.error('Error updating message:', error);
      setError('Failed to update message');
    }
  };

  return (
    <div className="chat-page-container">
      <ProfileRecipientPopup
        isOpen={showRecipientPopup}
        onClose={() => setShowRecipientPopup(false)}
        onSelectRecipient={handleRecipientSelect}
        currentUser={user}
      />
      
      {/* Profile Card Large */}
      {showProfileCard && selectedProfile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            position: 'relative',
            maxWidth: '400px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <HomeProfileCardLarge
              theme={selectedProfile.theme}
              flavour={selectedProfile.flavour}
              filling={selectedProfile.filling}
              cover={selectedProfile.cover}
              sponge={selectedProfile.sponge}
              cost={selectedProfile.cost}
              image={selectedProfile.image}
              cshape={selectedProfile.cshape}
              onClose={handleCloseProfileCard}
              onAddToFavourites={async () => {
                if (!selectedProfile) return;
                
                await addToFavourites({
                  id: selectedProfile.profileId,
                  theme: selectedProfile.theme,
                  cflavour: selectedProfile.flavour,
                  cfilling: selectedProfile.filling,
                  cover: selectedProfile.cover,
                  sponge: selectedProfile.sponge,
                  ccost: selectedProfile.cost,
                  cimage: selectedProfile.image,
                });
              }}
              onAddToCustom={async () => {
                if (!selectedProfile) return;

                await addToCustom({
                  id: selectedProfile.profileId,
                  theme: selectedProfile.theme,
                  cflavour: selectedProfile.flavour,
                  cfilling: selectedProfile.filling,
                  cover: selectedProfile.cover,
                  sponge: selectedProfile.sponge,
                  ccost: selectedProfile.cost,
                  cimage: selectedProfile.image,
                });
              }}
              isInFavourites={selectedProfile ? isInFavourites(selectedProfile.profileId) : false}
              isInCustom={selectedProfile ? isInCustom(selectedProfile.profileId) : false}
              user={user}
              userRole={userRole}
              fromFavourites={false}
              profileId={selectedProfile.profileId}
              isVisible={true}
            />
          </div>
        </div>
      )}
    <div className="chat-page-content">
      {/* Header with Calendar, Search, and Close in one row */}
      <div className="chat-header">
        <button
          className="calendar-filter-btn"
          onClick={() => setShowTimeFilters(!showTimeFilters)}
        >
          <FaCalendar />
        </button>

        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={selectedChat ? "Search by Phrase or Date (dd/mm/yyyy)" : "Search users by name, email or role..."}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="search-clear-btn"
              onClick={() => setSearchTerm('')}
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Close button - always visible in same line */}
        <button type="button" className="chat-close-btn" onClick={selectedChat ? handleCloseChat : handleClose}>
          <FaTimes />
        </button>

        {/* Time filter dropdown */}
        {showTimeFilters && (
          <div className="time-filter-options">
            <button
              className={timeFilter === 'all' ? 'active' : ''}
              onClick={() => {
                setTimeFilter('all');
                setShowTimeFilters(false);
              }}
            >
              All Time
            </button>
            <button
              className={timeFilter === 'lastWeek' ? 'active' : ''}
              onClick={() => {
                setTimeFilter('lastWeek');
                setShowTimeFilters(false);
              }}
            >
              Last Week
            </button>
            <button
              className={timeFilter === 'lastMonth' ? 'active' : ''}
              onClick={() => {
                setTimeFilter('lastMonth');
                setShowTimeFilters(false);
              }}
            >
              Last Month
            </button>
            <button
              className={timeFilter === 'lastYear' ? 'active' : ''}
              onClick={() => {
                setTimeFilter('lastYear');
                setShowTimeFilters(false);
              }}
            >
              Last Year
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="chat-error">
          <FaExclamationTriangle />
          {error}
          <button onClick={retryConnection} className="retry-btn">
            <FaSync />
          </button>
        </div>
      )}

      {/* Filter Info */}
      {(timeFilter !== 'all' || searchTerm) && (
        <div className="filter-info">
          {selectedChat ? (
            <>
              Showing {getFilteredMessages().length} of {selectedChat?.messages?.length || 0} messages
            </>
          ) : (
            <>
              Showing {filteredChats.length} of {chats.length} chats
            </>
          )}
          <button
            className="clear-filters-btn"
            onClick={() => {
              setTimeFilter('all');
              setSearchTerm('');
            }}
          >
            Clear filters
          </button>
        </div>
      )}

      {selectedChat ? (
        // INDIVIDUAL CHAT VIEW
        <div className="chat-full-view">
          {/* User Name Header between search bar and chat window */}
          <div className="chat-user-header">
            <div className="user-header-info">
              <span className="user-header-name">
                {getUserDisplayName(selectedChat)}
              </span>
            </div>
          </div>

          <div className="chat-messages-container">
            {!selectedChat.messages || selectedChat.messages.length === 0 ? (
              <div className="empty-chat">
                <FaComments size={48} color="#ddd" />
                <p>No messages yet</p>
                <small>Start a conversation with {selectedChat.userName}</small>
              </div>
            ) : (
              (() => {
                const _msgs = getFilteredMessages();
                const _lastOwn = [..._msgs].reverse().find(m => m.senderId === user.uid);
                const lastOwnMessageId = _lastOwn?.id || _lastOwn?.timestamp;
                return groupMessagesByDate(_msgs).map((group, groupIndex) => (
                <div key={groupIndex} className="date-group">
                  <div className="date-divider">
                    <span className="date-label">{group.label}</span>
                  </div>
                  {group.messages.map((msg) => {
                    const isOwnMessage = msg.senderId === user.uid;
                    const displayName = getDisplayName(msg, isOwnMessage);
                    const isEditing = editingMessageId === msg.id;
                    const isLastOwn = isOwnMessage && (msg.id || msg.timestamp) === lastOwnMessageId;

                    return (
                      <div
                        key={msg.id || msg.timestamp}
                        className={`message ${isOwnMessage ? 'admin-message' : 'user-message'}`}
                      >
                        <div className="message-bubble-row">
                        <div className="message-bubble">
                          {isEditing ? (
                            // Edit mode
                            <div className="edit-message-container">
                              <input
                                type="text"
                                value={editMessageText}
                                onChange={(e) => setEditMessageText(e.target.value)}
                                className="edit-message-input"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveEdit(msg);
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                              />
                              <div className="edit-actions">
                                <button
                                  onClick={() => handleSaveEdit(msg)}
                                  className="save-edit-btn"
                                  disabled={!editMessageText.trim()}
                                >
                                  <FaCheck />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="cancel-edit-btn"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Normal message display
                            <>
                              {msg.type === 'file' ? (
                                <FileMessage
                                  chatId={selectedChat.id}
                                  messageId={msg.id}
                                  signedUrl={msg.fileData?.downloadUrl ?? ''}
                                  mimeType={msg.fileData?.mimeType}
                                  fileName={msg.fileData?.name ?? 'Attachment'}
                                  fileSize={msg.fileData?.size}
                                  onClick={msg.fileData?.profileData ? () => handleProfileImageClick(msg) : undefined}
                                  isProfileImage={!!msg.fileData?.profileData}
                                />
                              ) : (
                                msg.text
                              )}
                            </>
                          )}
                        </div>
                        </div>
                        
                        <div className="message-meta">
                          {!isOwnMessage && (
                            <span className="message-sender">
                              {displayName}
                            </span>
                          )}
                          <span className="message-time">
                            <FaRegClock className="time-icon" />
                            {formatChatTime(msg.timestamp)}
                            {isOwnMessage && isLastOwn && (
                              <>
                                {(() => {
                                  if (msg.read) return <FaEye style={{ color: '#aaa', fontSize: 9, marginLeft: 4 }} title="Read" />;
                                  const now = Date.now();
                                  const lastSeenAge = receiverLastSeenRaw ? now - new Date(receiverLastSeenRaw).getTime() : null;
                                  let dotColor;
                                  if (receiverIsActive === true && lastSeenAge !== null) {
                                    if (lastSeenAge > 3 * 24 * 60 * 60 * 1000) {
                                      dotColor = '#FFC107'; // yellow: active but lastSeen > 3 days
                                    } else {
                                      dotColor = '#2196F3'; // blue: active, lastSeen <= 3 days
                                    }
                                  } else {
                                    dotColor = '#aaa'; // grey: no data or isactive false
                                  }
                                  return <FaCircle style={{ color: dotColor, fontSize: 7, marginLeft: 4 }} title="Sent" />;
                                })()}
                                {msg.read && msg.readAt && (
                                  <span style={{ marginLeft: 2, fontSize: 10, color: '#aaa' }}>
                                    {formatChatTime(msg.readAt)}
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                          {/* Message options button - only show for Admin/Super and not in edit mode */}
                          {!isEditing && (userRole === 'Admin' || userRole === 'Super') && (
                            <button
                              className="message-options-btn"
                              onClick={(e) => handleMessageOptionsClick(msg.id, e)}
                            >
                              <FaEllipsisH />
                            </button>
                          )}
                        </div>

                        {/* Message options menu */}
                        {messageMenuOpen && messageMenuOpen.messageId === msg.id && (
                          <div 
                            className="message-options-menu"
                            style={{
                              position: 'absolute',
                              zIndex: 1000
                            }}
                          >
                            <button onClick={() => handleCopyMessage(msg)} className="menu-option">
                              <FaCopy className="menu-icon" />
                            </button>
                            
                            {/* Only allow editing text messages, not file messages */}
                            {msg.type === 'text' && (
                              <button onClick={() => handleEditMessage(msg)} className="menu-option">
                                <FaEdit className="menu-icon" />
                              </button>
                            )}
                            
                            <button onClick={() => {setDeleteConfirmId(msg.id); setMessageMenuOpen(null)}} className="menu-option delete">
                              <FaTrash className="menu-icon" />
                            </button>
                          </div>
                        )}
                        {/* Delete Confirmation */}
                        {deleteConfirmId === msg.id && (
                          <div 
                            className="delete-confirmation"
                          >
                            <div className="delete-actions">
                              <button onClick={() => handleDeleteMessage(msg)} className="confirm-btn" title="Confirm">
                                <FaCheck />
                              </button>
                              <button onClick={() => setDeleteConfirmId(null)} className="cancel-btn" title="Cancel">
                                <FaTimes/>
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Copy Toast */}
                        {copyToastVisible && (
                          <div className="copy-toast">
                            Copied to Clipboard
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ));
              })()
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Profile image sending progress bar */}
          {isSendingProfile && (
            <div className="profile-send-progress">
              <span>Sending cake profile to chat�</span>
              <div className="profile-send-bar">
                <div className="profile-send-bar-fill" />
              </div>
            </div>
          )}

          {/* Message Input Form with Emoji and File Attachment */}
          <form className="chat-input-form" onSubmit={sendMessage}>
            <div className="input-container">
              <button
                type="button"
                className="attachment-btn"
                onClick={handleAttachmentClick}
                disabled={sending}
              >
                <FaPaperclip />
              </button>

              <button
                type="button"
                className="emoji-btn-inside"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                disabled={sending}
              >
                <FaSmile />
              </button>

              <div className="input-with-emoji">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Type your message to ${selectedChat.userName}...`}
                  className="chat-input"
                  disabled={sending}
                />
              </div>

              <button
                type="submit"
                className="chat-send-btn"
                disabled={!message.trim() || sending}
              >
                <FaPaperPlane />
              </button>
            </div>

            {/* Emoji picker dropdown */}
            {showEmojiPicker && (
              <div className="emoji-picker-container" ref={emojiPickerRef}>
                <div className="emoji-picker">
                  <div className="emoji-grid">
                                                            {['\ud83d\udcde','\u274c','\ud83d\udcac','\ud83d\udce7','\ud83d\udc4b','\u2705',
                      '\u2764\ufe0f','\ud83d\udc93','\ud83d\udc96','\ud83d\udc9d','\ud83d\udc8c','\ud83e\udd70',
                      '\ud83c\udf89','\ud83c\udf82','\ud83d\udd6f\ufe0f','\ud83c\udf81','\ud83d\udc90','\ud83e\udd42',
                      '\ud83d\udc4d','\ud83d\udc4f','\u2b50','\ud83d\udd25','\ud83d\udcaf','\ud83e\udd1d',
                      '\ud83d\ude0a','\ud83d\ude18','\ud83e\udd24','\ud83d\ude0d','\ud83d\ude4f','\ud83e\udd14'].map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        className="emoji-option"
                        onClick={() => handleEmojiSelect(emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </form>

          {/* File Insert Modal */}
          <ChatInsertModal
            isOpen={isInsertModalOpen}
            onClose={() => setIsInsertModalOpen(false)}
            onFileInsert={handleFileInsert}
          />
        </div>
      ) : (
        // CHAT LIST VIEW
        <div className="chats-list-container">
          {loading && (
            <div className="loading-simple">
              <div className="loading-spinner"></div>
              Loading chats...
            </div>
          )}

          {!loading && filteredChats.length > 0 && (
            <div className="chats-section">
              <div className="section-header">
                Active Chats ({filteredChats.length})
              </div>
              {filteredChats
                .sort((a, b) => new Date(b.updated) - new Date(a.updated))
                .map(chat => {
                  const lastMessage = getLastMessage(chat);
                  const unreadCount = chat.messages?.filter(msg =>
                    !msg.read && msg.senderId !== user.uid
                  ).length || 0;

                  return (
                    <div
                      key={chat.id}
                      className={`chat-item ${selectedChatId === chat.id ? 'active' : ''}`}
                      onClick={() => startChat(chat)}
                    >
                      <div className="chat-avatar">
                        {getProfileImage(chat.profilePicture) ? (
                          <img
                            src={getProfileImage(chat.profilePicture)}
                            alt="Profile"
                            className="profile-photo"
                          />
                        ) : (
                          <div className="avatar-placeholder">
                            {chat.userName?.charAt(0) || 'U'}
                          </div>
                        )}
                      </div>

                      <div className="chat-info">
                        <div className="chat-header">
                          <div className="user-info">
                            <span className="user-name">
                              {getUserDisplayName(chat)}
                            </span>
                            <span className="user-email">
                              {chat.email}
                            </span>
                          </div>
                          <div className="chat-time-info">
                            <span className="last-seen">
                              {(() => {
                                const lastUserMsg = chat.messages
                                  ? [...chat.messages].reverse().find(m => m.senderId !== user?.uid)
                                  : null;
                                const lastUserMsgTime = lastUserMsg?.timestamp ? new Date(lastUserMsg.timestamp).getTime() : null;
                                const lastSeenTime = chat.lastSeenRaw ? new Date(chat.lastSeenRaw).getTime() : null;
                                const now = Date.now();

                                const greenByMsg = lastUserMsgTime !== null && now - lastUserMsgTime < 5 * 60 * 1000;
                                const greenByLastSeen = !greenByMsg && lastSeenTime !== null && now - lastSeenTime < 5 * 60 * 1000
                                  && (lastUserMsgTime === null || lastSeenTime >= lastUserMsgTime);

                                // For blue/yellow/grey: show lastSeen unless last user msg is more recent
                                const fallbackTime = (lastUserMsgTime !== null && (lastSeenTime === null || lastUserMsgTime > lastSeenTime))
                                  ? formatChatTime(lastUserMsg.timestamp)
                                  : chat.lastSeen;

                                let dotColor;
                                let displayTime = chat.lastSeen;
                                if (greenByMsg) {
                                  dotColor = '#4CAF50';
                                  displayTime = lastUserMsg?.timestamp ? formatChatTime(lastUserMsg.timestamp) : chat.lastSeen;
                                } else if (greenByLastSeen) {
                                  dotColor = '#4CAF50';
                                  // displayTime stays chat.lastSeen
                                } else {
                                  const lastSeenAge = chat.lastSeenRaw ? now - new Date(chat.lastSeenRaw).getTime() : null;
                                  if (chat.isActiveRaw === true && lastSeenAge !== null) {
                                    if (lastSeenAge > 3 * 24 * 60 * 60 * 1000) {
                                      dotColor = '#FFC107'; // yellow: active, lastSeen > 3 days
                                    } else {
                                      dotColor = '#2196F3'; // blue: active, lastSeen ≤ 3 days
                                    }
                                  } else {
                                    dotColor = '#aaa'; // grey: no presence data or isactive false
                                  }
                                  displayTime = fallbackTime;
                                }
                                return <><FaCircle style={{ fontSize: 7, color: dotColor, marginRight: 4 }} />{displayTime}</>;
                              })()}
                            </span>
                            <button 
                              className={`bookmark-btn ${chat.bookmarked ? 'bookmarked' : ''}`}
                              onClick={(e) => toggleBookmark(chat.id, e)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="chat-preview">
                          <span className="last-message">
                            {lastMessage.timestamp && (
                              <span className="last-msg-time">
                                {formatChatTime(lastMessage.timestamp)}
                              </span>
                            )}
                            {lastMessage.senderId && (
                              <span className="last-msg-sender">
                                {lastMessage.senderId === user?.uid ? 'me'
                                  : (lastMessage.senderRole === 'Admin' || lastMessage.senderRole === 'Super') ? lastMessage.senderRole
                                  : 'user'}:{' '}
                              </span>
                            )}
                            <span className="last-msg-text">
                              {lastMessage.text?.length > 22
                                ? lastMessage.text.substring(0, 22) + '...'
                                : lastMessage.text
                              }
                            </span>
                            {unreadCount > 0 && (
                              <span className="unread-badge">{unreadCount}</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* Search Results for Both Admin and Super */}
          {searchTerm.trim() && searchResults.length > 0 && (
            <div className="chats-section">
              <div className="section-header">
                Search Results ({searchResults.length})
              </div>
              {searchResults.map(userItem => (
                <div
                  key={userItem.id}
                  className={`chat-item search-result ${userItem.hasExistingChat ? 'has-existing-chat' : ''}`}
                  onClick={() => startChat(userItem)}
                >
                  <div className="chat-avatar">
                    {getProfileImage(userItem.profilePicture) ? (
                      <img
                        src={getProfileImage(userItem.profilePicture)}
                        alt="Profile"
                        className="profile-photo"
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {userItem.userName?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>

                  <div className="chat-info">
                    <div className="chat-header">
                      <div className="user-info">
                        <span className="user-name">
                          {getUserDisplayName(userItem)}
                        </span>
                        <span className="user-email">
                          {userItem.email}
                        </span>
                      </div>
                      <span className={`chat-status ${userItem.hasExistingChat ? 'existing-chat' : 'new-chat'}`}>
                        {userItem.hasExistingChat ? `Continue Chat (${userItem.firebaseMessages} messages)` : 'New Chat'}
                      </span>
                    </div>
                    
                    {userItem.hasExistingChat && (
                      <div className="chat-preview">
                        <p className="last-message">
                          <FaRegComment className="message-icon" />
                          {userItem.existingChat?.messages?.length > 0 
                            ? `Last: ${userItem.existingChat.messages[userItem.existingChat.messages.length - 1]?.text?.substring(0, 30)}...`
                            : 'No messages yet'
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty States */}
          {!loading && filteredChats.length === 0 && (
            <div className="empty-state">
              {searchTerm.trim() || timeFilter !== 'all' ? (
                <>
                  <FaSearch size={32} color="#ccc" />
                  <p>No chats found</p>
                  <small>
                    {searchTerm.trim() && timeFilter !== 'all' 
                      ? 'Try adjusting your search terms or time period'
                      : searchTerm.trim() 
                      ? 'Try adjusting your search terms'
                      : 'No chats found in the selected time period'
                    }
                  </small>
                </>
              ) : (
                <>
                  <FaComments size={32} color="#ccc" />
                  <p>No active chats</p>
                  <small>
                    {userRole === 'Super'
                      ? 'Search for users to start a conversation'
                      : 'Users will appear here when they message you'
                    }
                  </small>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>

    <style>{`

      .chat-page-container {
        display: flex;
        flex-direction: column;
        padding: 5px;
        padding-bottom: 50px;
        background-color: var(--bg-primary, #f5f5f5);
        width: 100%;
        box-sizing: border-box;
        min-height: 100vh;
      }

      .chat-page-content {
        position: relative;
        background: var(--bg-surface, white);
        border-radius: 8px;
        width: 85vw;
        max-width: 550px;
        height: calc(100vh - 70px);
        padding: 15px;
        margin: 10px auto;
        display: flex;
        flex-direction: column;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        box-sizing: border-box;
        overflow: hidden;
      }

      /* Chat Header - All in one row */
      .chat-header {
        display: flex;
        align-items: center;
        padding: 10px 8px;
        position: relative;
        gap: 8px;
      }

      .calendar-filter-btn {
        background: none;
        border: none;
        cursor: pointer;
        color: #666;
        font-size: 16px;
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: color 0.2s;
        flex-shrink: 0;
      }

      .calendar-filter-btn:hover {
        color: #fa4848;
      }

      .search-input-wrapper {
        flex: 1;
        background: #f0f2f5;
        border-radius: 8px;
        padding: 5px;
        display: flex;
        align-items: center;
        position: relative;
        margin: 0;
      }

      .search-icon {
        margin: 0 5px 0 5px;
        color: #666;
        font-size: 10px;
      }

      .search-input {
        flex: 1;
        padding: 4px 4px 4px 0;
        border: none;
        background: transparent;
        font-size: 10px;
        outline: none;
        color: #333;
      }

      .search-input::placeholder {
        color: #666;
        font-size: 10px;
      }

      .search-clear-btn {
        background: none;
        border: none;
        cursor: pointer;
        color: #666;
        font-size: 10px;
        padding: 4px;
        margin-left: 5px;
      }

      .chat-close-btn {
        background: none;
        border: none;
        font-size: 16px;
        cursor: pointer;
        color: var(--text-secondary, #666);
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .chat-close-btn:hover {
        color: var(--text-primary, #333);
      }

      /* Remove focus ring after mouse click; keep it for keyboard navigation */
      .chat-close-btn:focus:not(:focus-visible) {
        outline: none;
      }

      /* Time Filter Options */
      .time-filter-options {
        position: absolute;
        top: 100%;
        left: 0;
        background: var(--bg-surface, white);
        border: 1px solid var(--border-color, #ddd);
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        z-index: 100;
        margin-top: 5px;
        min-width: 120px;
      }

      .time-filter-options button {
        display: block;
        width: 100%;
        padding: 10px 12px;
        background: none;
        border: none;
        text-align: left;
        cursor: pointer;
        font-size: 14px;
        color: var(--text-primary, #333);
        border-bottom: 1px solid var(--border-color, #f0f0f0);
      }

      .time-filter-options button:last-child {
        border-bottom: none;
      }

      .time-filter-options button:hover {
        background: #f5f5f5;
      }

      .time-filter-options button.active {
        background: #fa4848;
        color: white;
      }

      .chat-error {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 12px;
        color: #ff4444;
        background: #ffeaea;
        padding: 5px 10px;
        border-radius: 4px;
        margin-bottom: 15px;
      }

      .retry-btn {
        background: none;
        border: none;
        color: #ff4444;
        cursor: pointer;
        padding: 2px;
        border-radius: 3px;
      }

      /* Filter Info */
      .filter-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
        color: var(--text-secondary, #666);
        margin-bottom: 10px;
        padding: 5px 0;
      }

      .clear-filters-btn {
        background: none;
        border: none;
        color: #fa4848;
        cursor: pointer;
        font-size: 12px;
        text-decoration: underline;
      }

      /* Chat Full View */
      .chat-full-view {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      /* Chat User Header (for individual chat view) */
      .chat-user-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 0;
        margin-bottom: 10px;
        border-bottom: 1px solid var(--border-color, #f0f0f0);
      }

      .user-header-info {
        display: flex;
        flex-direction: column;
      }

      .user-header-name {
        font-weight: 600;
        font-size: 16px;
        color: var(--text-primary, #333);
      }

      .user-header-status {
        display: flex;
        align-items: center;
      }

      .last-seen-header {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: var(--text-secondary, #666);
      }

      .last-seen-header .eye-icon {
        font-size: 11px;
      }

      .chat-messages-container {
        flex: 1;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        padding: 10px;
        background: var(--bg-section, #f8f9fa);
        border-radius: 8px;
        margin-bottom: 15px;
        min-height: 0;
        scrollbar-width: none;
      }

      .chat-messages-container::-webkit-scrollbar {
        display: none;
      }

      .date-group {
        margin-bottom: 10px;
      }

      .date-divider {
        display: flex;
        align-items: center;
        margin: 20px 0;
        text-align: center;
      }

      .date-divider::before,
      .date-divider::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid var(--border-color, #e0e0e0);
      }

      .date-label {
        padding: 0 15px;
        font-size: 12px;
        color: var(--text-secondary, #666);
        background: var(--bg-section, #f8f9fa);
        border-radius: 12px;
        margin: 0 10px;
      }

      .message {
        position: relative;
        margin-bottom: 15px;
        max-width: 85%;
      }

      .admin-message {
        margin-left: auto;
      }

      .admin-message .message-bubble {
        background: var(--bg-surface, white);
        color: var(--text-primary, #333);
        padding: 10px 15px;
        border-radius: 18px 18px 5px 18px;
        border: 1px solid var(--border-color, #e0e0e0);
        position: relative;
      }

      .user-message .message-bubble {
        background: #fde8e8;
        color: #333;
        padding: 10px 15px;
        border-radius: 18px 18px 18px 5px;
        border: 1px solid #f5c8c8;
      }

      .message-bubble-row {
        display: flex;
        align-items: flex-end;
        gap: 3px;
      }
      .message-bubble-row .message-bubble {
        flex: 1;
        min-width: 0;
      }
      .msg-status-icon {
        flex-shrink: 0;
        margin-bottom: 4px;
        line-height: 1;
      }
      .msg-status-read {
        color: #2196F3;
        font-size: 7px;
      }
      .msg-status-delivered {
        color: #4CAF50;
        font-size: 7px;
      }

      .message-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 4px;
        font-size: 11px;
        color: var(--text-muted, #888);
        position: relative;
      }

      .admin-message .message-meta {
        flex-direction: row-reverse;
      }

      .message-sender {
        font-weight: 500;
      }

      .message-time {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .time-icon {
        font-size: 9px;
        opacity: 0.7;
      }

      /* File Message Styles */
      .file-message {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 8px;
        background: var(--bg-section, #f8f9fa);
        border-radius: 6px;
        cursor: pointer;
        transition: background-color 0.2s;
        max-width: 300px;
        margin: 5px 0;
      }

      .file-message:hover {
        background: #e9ecef;
      }

      .image-preview {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .image-preview img {
        max-width: 100%;
        max-height: 200px;
        border-radius: 4px;
        object-fit: contain;
        display: block;
      }

      .image-fallback {
        text-align: center;
        font-size: 14px;
        padding: 20px;
        background: #e9ecef;
        border-radius: 4px;
        display: none;
      }

      .file-info-only {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px;
      }

      .file-icon {
        font-size: 20px;
      }

      .file-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
      }

      .file-name {
        font-weight: 500;
        font-size: 14px;
        word-break: break-word;
      }

      .file-size {
        font-size: 12px;
        color: var(--text-secondary, #666);
      }

      .file-link {
        color: var(--text-primary, #333);
        text-decoration: none;
        font-weight: 500;
      }

      .file-link:hover {
        text-decoration: underline;
        color: #fa4848;
      }

      /* Chat Input Form - All in one line */
      .chat-input-form {
        width: 100%;
        position: relative;
      }

      .input-container {
        display: flex;
        align-items: center;
        gap: 6px;
        width: 100%;
      }

      .attachment-btn {
        background: none;
        color: var(--text-secondary, #666);
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        flex-shrink: 0;
      }

      .attachment-btn:hover:not(:disabled) {
        color: #fa4848;
      }

      .attachment-btn:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }

      /* NEW: Input with emoji button inside */
      .input-with-emoji {
        flex: 1;
        position: relative;
        display: flex;
        align-items: center;
      }

      .chat-input {
        flex: 1;
        padding: 12px 15px;
        border: 1px solid var(--border-color, #ddd);
        border-radius: 25px;
        outline: none;
        font-size: 14px;
        min-width: 0;
        width: 100%;
        box-sizing: border-box;
      }

      .chat-input:focus {
        border-color: #fa4848;
      }

      /* Emoji button beside attachment button */
      .emoji-btn-inside {
        background: none;
        border: none;
        color: var(--text-secondary, #666);
        cursor: pointer;
        padding: 5px;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        flex-shrink: 0;
      }

      .emoji-btn-inside:hover:not(:disabled) {
        color: #fa4848;
      }

      .emoji-btn-inside:disabled {
        color: #ccc;
        cursor: not-allowed;
      }

      /* FIXED: Emoji Picker Styles - Contained within window */
      .emoji-picker-container {
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 0;
        background: var(--bg-surface, white);
        border: 1px solid var(--border-color, #ddd);
        border-radius: 12px;
        box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        z-index: 100;
        margin-bottom: 10px;
        overflow: hidden;
      }

      .emoji-picker {
        width: 100%;
        padding: 10px;
        box-sizing: border-box;
      }

      .emoji-grid {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        gap: 4px;
      }

      .emoji-option {
        background: none;
        border: none;
        font-size: 22px;
        cursor: pointer;
        padding: 6px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 44px;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }

      .emoji-option:active {
        background: #f0f0f0;
        transform: scale(1.15);
      }

      .chat-send-btn {
        background: #fa4848;
        color: white;
        border: none;
        border-radius: 50%;
        width: 45px;
        height: 45px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.2s;
        flex-shrink: 0;
      }

      .chat-send-btn:hover:not(:disabled) {
        background: #e03e3e;
      }

      .chat-send-btn:disabled {
        background: #ccc;
        cursor: not-allowed;
      }

      /* Chats List Container */
      .chats-list-container {
        flex: 1;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        scrollbar-width: none;
      }

      .chats-list-container::-webkit-scrollbar {
        display: none;
      }

      .loading-simple {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 40px 20px;
        color: var(--text-secondary, #666);
        font-size: 14px;
      }

      .loading-spinner {
        width: 20px;
        height: 20px;
        border: 2px solid #f3f3f3;
        border-top: 2px solid #fa4848;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      .chats-section {
        margin-bottom: 20px;
      }

      .section-header {
        padding: 12px 0;
        font-size: 13px;
        font-weight: 600;
        color: var(--text-secondary, #666);
        background: var(--bg-surface, white);
        border-bottom: 1px solid var(--border-color, #f0f0f0);
        margin-bottom: 10px;
      }

      .chat-item {
        display: flex;
        align-items: center;
        padding: 10px 0;
        gap: 12px;
        cursor: pointer;
        border-bottom: 1px solid var(--border-color, #f0f0f0);
        transition: background 0.2s;
      }

      .chat-item:hover {
        background: rgba(0, 0, 0, 0.04);
      }

      .chat-item.active {
        background: rgba(250, 71, 71, 0.08);
      }

      .chat-item.search-result {
        opacity: 0.8;
      }

      .chat-avatar {
        flex-shrink: 0;
      }

      .profile-photo {
        width: 45px;
        height: 45px;
        border-radius: 50%;
        object-fit: cover;
      }

      .avatar-placeholder {
        width: 45px;
        height: 45px;
        border-radius: 50%;
        background: var(--bg-section, #e0e0e0);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-secondary, #666);
        font-weight: 500;
        font-size: 16px;
      }

      .chat-info {
        flex: 1;
        min-width: 0;
      }


      /* User Info Layout */
      .user-info {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0;
        max-width: 45%;
      }

      .user-name {
        font-weight: 600;
        font-size: 14px;
        color: var(--text-primary, #333);
        margin-bottom: 1px;
        line-height: 1.2;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-email {
        font-size: 11px;
        color: var(--text-secondary, #666);
        font-weight: 400;
        line-height: 1.2;white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Chat Time Info */
      .chat-time-info {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        position: relative;
        padding-right: 24px;
        min-width: 0;
      }

      .last-seen {
        display: flex;
        align-items: center;
        gap: 2px;
        font-size: 10px;
        color: var(--text-secondary, #666);
        white-space: nowrap;
        line-height: 1.1;
      }

      .bookmark-btn {
        background: transparent !important;
        border: none;
        cursor: pointer;
        padding: 4px;
        border-radius: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fa4848;
        opacity: 0.7;
        position: absolute;
        -webkit-appearance: none;
        appearance: none;
        top: 50%;
        right: -20px;                  
        transform: translateY(-50%);
        width: 20px;                
        height: 20px;                
        z-index: 10;
      }

      .bookmark-btn:hover,
      .bookmark-btn:focus,
      .bookmark-btn:active {
        background: transparent !important;
        outline: none;
      }

      .bookmark-btn:not(.bookmarked) {
        color: #ccc;
        opacity: 0.8;
      }

      .chat-preview {
        display: flex;
        align-items: center;
        margin-top: 4px;
        overflow: hidden;
      }

      .last-message {
        margin: 0;
        font-size: 12px;
        color: var(--text-secondary, #666);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .last-msg-sender {
        font-weight: 600;
        color: var(--text-primary, #333);
        flex-shrink: 0;
        font-size: 12px;
      }

      .last-msg-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
      }

      .last-msg-time {
        font-size: 10px;
        color: var(--text-secondary, #888);
        white-space: nowrap;
        flex-shrink: 0;
      }

      .message-icon {
        font-size: 10px;
        opacity: 0.6;
        flex-shrink: 0;
      }

      .unread-badge {
        background: #f44336;
        color: white;
        font-size: 10px;
        padding: 2px 6px;
        border-radius: 8px;
        min-width: 16px;
        text-align: center;
        font-weight: 500;
      }

      /* Chat Status Labels */
      .chat-status {
        font-size: 11px;
        padding: 2px 6px;
        border-radius: 8px;
        font-weight: 500;
        white-space: nowrap;
      }

      .chat-status.existing-chat {
        color: #1976d2;
        background: #e3f2fd;
        border: 1px solid #bbdefb;
      }

      .chat-status.new-chat {
        color: #388e3c;
        background: #e8f5e8;
        border: 1px solid #c8e6c9;
      }

      /* Empty States */
      .empty-chat {
        text-align: center;
        color: var(--text-secondary, #666);
        padding: 40px 20px;
      }

      .empty-chat-icon {
        font-size: 48px;
        color: #ddd;
        margin-bottom: 10px;
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 60px 20px;
        text-align: center;
        color: var(--text-secondary, #666);
      }

      .empty-state p {
        margin: 12px 0 4px 0;
        font-weight: 500;
        font-size: 14px;
      }

      .empty-state small {
        font-size: 12px;
        color: var(--text-muted, #999);
      }

      .message-options-btn {
        background: transparent !important; 
        border: none !important;  
        color: var(--text-secondary, #666); 
        cursor: pointer;
        padding: 2px !important;
        border-radius: 1px;
        opacity: 1; 
        transition: opacity 0.15s ease, color 0.15s ease;
      }

      .message:hover .message-options-btn {
        color: #fa4848;
      }

      .message-options-menu {
        background: var(--bg-surface, #fff);
        border: 1px solid var(--border-color, #ddd);
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0,0,0,.15);
        padding: 4px;
        display: flex;
        gap: 4px;
        z-index: 1000;
      }

      .menu-option {
        background: transparent !important;   /* no background */
        border: none !important;
        color: var(--text-secondary, #666);  /* icon colour */
        cursor: pointer;
        padding: 4px !important;
        border-radius: 4px;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease;
      }

      .menu-option:hover {
        background: #f5f5f5 !important;
        color: #fa4848;                     /* accent colour */
      }

      .menu-option.delete:hover {
        background: #fff5f5 !important;
        color: #ff4444;
      }

      .copy-toast {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #666;
        color: #fff5f5;
        padding: 12px 20px;
        border-radius: 6px;
        font-size: 14px;
        z-index: 10000;
        animation: fadeInOut 2s ease-in-out;
      }
      
      .confirm-btn,
      .cancel-btn {
        background: transparent !important;
        border: none !important;         
        color: #666;
        cursor: pointer;
        padding: 6px 10px !important;
        border-radius: 4px;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease;
      }

      .confirm-btn:hover {
        background: none !important; 
        color: #fa4848 !important;
      }
      .cancel-btn:hover {
        background: none !important;
        color: #333;
      }

      .delete-actions {
        display: flex;
        gap: 12px;
      }

      .edit-actions {
        display: flex;
        gap: 12px; 
      }

      .save-edit-btn,
      .cancel-edit-btn {
        background: transparent !important; 
        border: none !important; 
        color: #666; 
        cursor: pointer;
        padding: 6px 10px !important;
        border-radius: 4px;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease;
      }

      .save-edit-btn:hover {
        background: none !important;
        color: #fa4848 !important;
      }
      .cancel-edit-btn:hover {
        background: none !important;
        color: #333;
      }

      @keyframes fadeInOut {
        0%   { opacity: 0; transform: translate(-50%,-50%) scale(0.8); }
        20%  { opacity: 1; transform: translate(-50%,-50%) scale(1);   }
        80%  { opacity: 1; transform: translate(-50%,-50%) scale(1);   }
        100% { opacity: 0; transform: translate(-50%,-50%) scale(0.8); }
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      .profile-send-progress {
        padding: 6px 12px 4px;
        background: #fff8f0;
        border-top: 1px solid #fde8c8;
        font-size: 12px;
        color: #c0662a;
        font-weight: 500;
      }

      .profile-send-bar {
        height: 4px;
        background: #fde8c8;
        border-radius: 2px;
        overflow: hidden;
        margin-top: 4px;
      }

      .profile-send-bar-fill {
        height: 100%;
        width: 40%;
        background: #fa4848;
        border-radius: 2px;
        animation: profileSendSlide 1.2s ease-in-out infinite;
      }

      @keyframes profileSendSlide {
        0%   { transform: translateX(-100%); }
        50%  { transform: translateX(150%); }
        100% { transform: translateX(-100%); }
      }

      /* -- Dark mode -- */
      html[data-theme="dark"] .chat-page-content {
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
      }
      html[data-theme="dark"] .chat-header {
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .calendar-filter-btn,
      html[data-theme="dark"] .chat-close-btn {
        color: var(--text-secondary);
      }
      html[data-theme="dark"] .calendar-filter-btn:hover,
      html[data-theme="dark"] .chat-close-btn:hover {
        color: var(--text-primary);
      }
      html[data-theme="dark"] .search-input-wrapper {
        background: var(--input-bg);
        border: 1px solid var(--border-color);
      }
      html[data-theme="dark"] .search-input {
        background: transparent;
        color: var(--text-primary);
      }
      html[data-theme="dark"] .search-input::placeholder {
        color: var(--text-muted);
      }
      html[data-theme="dark"] .search-clear-btn {
        color: var(--text-secondary);
      }
      html[data-theme="dark"] .time-filter-options {
        background: var(--bg-surface);
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .time-filter-options button {
        color: var(--text-primary);
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .time-filter-options button:hover {
        background: var(--bg-section);
      }
      html[data-theme="dark"] .filter-info {
        color: var(--text-muted);
      }
      html[data-theme="dark"] .chat-user-header {
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .user-header-name {
        color: var(--text-primary);
      }
      html[data-theme="dark"] .last-seen-header {
        color: var(--text-muted);
      }
      html[data-theme="dark"] .date-label {
        color: var(--text-muted);
        background: var(--bg-section);
      }
      html[data-theme="dark"] .date-divider::before,
      html[data-theme="dark"] .date-divider::after {
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .admin-message .message-bubble {
        background: #3a3a3a !important;
        color: #ececec !important;
        border: 1px solid #4a4a4a !important;
      }
      html[data-theme="dark"] .user-message .message-bubble {
        background: rgba(250, 71, 71, 0.18) !important;
        color: #ececec !important;
        border: 1px solid rgba(250, 71, 71, 0.25) !important;
      }
      html[data-theme="dark"] .message-meta {
        color: var(--text-muted);
      }
      html[data-theme="dark"] .section-header {
        color: var(--text-secondary);
        background: var(--bg-surface);
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .chat-item {
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .chat-item:hover {
        background: rgba(255,255,255,0.05);
      }
      html[data-theme="dark"] .chat-item.active {
        background: rgba(250,71,71,0.12);
      }
      html[data-theme="dark"] .user-name {
        color: var(--text-primary);
      }
      html[data-theme="dark"] .user-email,
      html[data-theme="dark"] .last-seen,
      html[data-theme="dark"] .last-message,
      html[data-theme="dark"] .last-msg-time,
      html[data-theme="dark"] .last-msg-sender {
        color: var(--text-muted);
      }
      html[data-theme="dark"] .avatar-placeholder {
        background: var(--bg-section);
        color: var(--text-secondary);
      }
      html[data-theme="dark"] .chat-status.existing-chat {
        background: rgba(25,118,210,0.15);
        color: #90caf9;
        border-color: rgba(25,118,210,0.3);
      }
      html[data-theme="dark"] .chat-status.new-chat {
        background: rgba(56,142,60,0.15);
        color: #a5d6a7;
        border-color: rgba(56,142,60,0.3);
      }
      html[data-theme="dark"] .chat-input {
        background: var(--input-bg);
        border-color: var(--border-color);
        color: var(--text-primary);
      }
      html[data-theme="dark"] .attachment-btn {
        background: var(--bg-section);
        border-color: var(--border-color);
        color: var(--text-secondary);
      }
      html[data-theme="dark"] .emoji-picker-container,
      html[data-theme="dark"] .emoji-picker {
        background: var(--bg-surface);
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .emoji-btn-inside {
        color: var(--text-secondary);
      }
      html[data-theme="dark"] .message-options-menu {
        background: var(--bg-surface);
        border-color: var(--border-color);
      }
      html[data-theme="dark"] .menu-option {
        color: var(--text-secondary);
      }
      html[data-theme="dark"] .menu-option:hover {
        background: var(--bg-section) !important;
        color: #fa4848;
      }
      html[data-theme="dark"] .file-message {
        background: var(--bg-section);
      }
      html[data-theme="dark"] .file-message:hover {
        background: var(--bg-primary);
      }
      html[data-theme="dark"] .empty-state,
      html[data-theme="dark"] .empty-chat {
        color: var(--text-secondary);
      }
      html[data-theme="dark"] .empty-state small {
        color: var(--text-muted);
      }

      @media (max-width: 450px) {
        .chat-page-content {
          width: 95vw;
          padding: 10px;
          margin: 5px auto;
          height: calc(100vh - 60px);
        }

        .message {
          position: relative;
          margin-bottom: 5px;
          max-width: 85%;
        }

        .input-container {
          gap: 4px;
        }

        .attachment-btn {
          width: 38px;
          height: 38px;
        }

        .emoji-btn-inside {
          width: 30px;
          height: 30px;
          right: 5px;
        }

        .chat-send-btn {
          width: 42px;
          height: 42px;
        }

        .emoji-picker-container {
          left: 0;
          right: 0;
        }

        .emoji-grid {
          grid-template-columns: repeat(6, 1fr);
        }

        .chat-input {
          padding: 10px 40px 10px 12px;
        }

        .profile-photo {
          width: 40px;
          height: 40px;
        }

        .avatar-placeholder {
          width: 40px;
          height: 40px;
          font-size: 14px;
        }

        .chat-user-header {
          padding: 6px 0;
          margin-bottom: 8px;
        }

        .user-header-name {
          font-size: 14px;
        }

        .last-seen-header {
          font-size: 11px;
        }

      }
    `}</style>
  </div>
);
};

export default AdminChat;